import React, { useEffect, useMemo, useState } from 'react'
import { makeStyles } from '@material-ui/core'
import CssBaseline from '@material-ui/core/CssBaseline'
import { withRouter } from 'react-router-dom'
import Amplify from '@aws-amplify/core'
import { Storage } from '@aws-amplify/storage'
import aws_exports from './aws-exports'
import {
  AmplifyAuthenticator,
  AmplifySignIn,
  AmplifySignUp
} from '@aws-amplify/ui-react'
import { AuthState, onAuthUIStateChange } from '@aws-amplify/ui-components'
import useMediaQuery from '@material-ui/core/useMediaQuery'
import {
  createTheme,
  ThemeProvider as MuiThemeProvider
} from '@material-ui/core/styles'

import { Elements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import LOG from './logging/Logger'
import Link from '@material-ui/core/Link'
import Typography from '@material-ui/core/Typography'
import GettingFocusedApp from './GettingFocusedApp'
import { DataStore } from '@aws-amplify/datastore'
import Splash from './components/splash/Splash'
import CookieWarning from './components/prompts/CookieWarning'
import GuestSignUp from './components/prompts/GuestSignUp'
import { createStripeCustomer } from './data/stripe/customer'
import BetaSignUp from './components/prompts/BetaSignUp'
import { isMobileOnly, withOrientationChange } from 'react-device-detect'
import qs from 'qs'
import { Helmet } from 'react-helmet'
import { useMountEffect } from './data/hooks'
import { getPublicKey } from './data/stripe/key'
import { isStaging } from './data/util/environment'

const GUEST_ACCESS_ONLY = false
const BETA_PHASE = false
const FORM_FIELDS = [
  {
    type: 'username',
    label: 'Email *',
    placeholder: 'Enter your email',
    required: true
  },
  {
    type: 'password',
    required: true
  }
]

const stripePromise = loadStripe(getPublicKey())

Amplify.configure(aws_exports)
Storage.configure({ level: 'private', serverSideEncryption: 'AES256' })

const useStyles = makeStyles(theme => ({
  '@global': {
    'html, body, #root': {
      height: '100%',
      // Without this, going from landscape to portrait on Safari,
      // with nav bar open, text zooms in and never back out
      // https://stackoverflow.com/questions/2710764/preserve-html-font-size-when-iphone-orientation-changes-from-portrait-to-landsca
      '-webkit-text-size-adjust': '100%'
    }
  },
  root: {
    display: 'flex',
    overflow: 'hidden'
  },
  help: {
    marginRight: theme.spacing(3)
  }
}))

function App({ history, isLandscape, location }) {
  LOG.debug('Render: Application')

  const classes = useStyles()

  const [authState, setAuthState] = useState()
  const [canSignIn, setCanSignIn] = useState(false)
  const [warnCookies, setWarnCookies] = useState(false)
  const [guestPrompt, setGuestPrompt] = useState(false)
  const [betaPrompt, setBetaPrompt] = useState(false)
  const [addGoogleConversion, setAddGoogleConversion] = useState(false)
  const [showSignUp, setShowSignUp] = useState(false)

  useMountEffect(() => {
    const signUp =
      qs.parse(location.search, { ignoreQueryPrefix: true }).signup === 'true'

    setShowSignUp(signUp)

    if (signUp) {
      const queryParams = new URLSearchParams(location.search)
      queryParams.delete('signup')
      history.replace({
        search: queryParams.toString()
      })
    }
  }, [])

  useEffect(() => {
    onAuthUIStateChange((nextAuthState, authData) => {
      LOG.info('AuthState', authState, nextAuthState)

      if (authState === 'confirmSignUp' && nextAuthState === 'signedin') {
        // Update google conversion
        setAddGoogleConversion(true)
      }

      if (authState === 'signedin') {
        if (
          authData &&
          authData.attributes &&
          !authData.attributes['custom:stripeCustId']
        ) {
          LOG.info('No stripe customer yet, set one up')
          createStripeCustomer(authData)
        }

        // Hiding the form, then reloading so that username/password
        // aren't cached in login form after logout
        if (nextAuthState === 'signin') {
          setCanSignIn(false)
          window.location.reload()
        }
      }

      switch (nextAuthState) {
        case 'signin':
          setWarnCookies(false)
          setGuestPrompt(GUEST_ACCESS_ONLY)
          DataStore.stop().then(() =>
            DataStore.clear().then(() => {
              setAuthState(nextAuthState)
              setCanSignIn(true)
            })
          )
          break
        case 'signup':
          setWarnCookies(true)
          setBetaPrompt(BETA_PHASE)
          DataStore.stop().then(() =>
            DataStore.clear().then(() => {
              setAuthState(nextAuthState)
              setCanSignIn(true)
            })
          )
          break
        default:
          setWarnCookies(false)
          setAuthState(nextAuthState)
          setCanSignIn(true)
          break
      }
    })
  }, [authState])

  const prefersLightMode = useMediaQuery('(prefers-color-scheme: light)', {
    noSsr: true
  })

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          type: prefersLightMode ? 'light' : 'dark',
          primary: {
            main: '#7cdefb'
          },
          secondary: {
            main: '#db005c'
          }
        },
        typography: {
          useNextVariants: true
        }
      }),
    [prefersLightMode]
  )

  return (
    <MuiThemeProvider theme={theme}>
      {authState !== AuthState.SignedIn && (
        <Typography align='right' color='secondary' className={classes.help}>
          <Link href='https://www.gettingfocused.com' color='inherit'>
            Home
          </Link>
        </Typography>
      )}
      {addGoogleConversion && (
        <Helmet>
          <script
            async
            src='https://www.googletagmanager.com/gtag/js?id=AW-388250256'
          ></script>
          <script>
            {`
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());

                gtag('config', 'AW-388250256');
              `}
          </script>
          <script>
            {`
              function gtag_report_conversion(url) {
                var callback = function () {
                  if (typeof(url) != 'undefined') {
                    window.location = url;
                  }
                };
                gtag('event', 'conversion', {
                    'send_to': 'AW-388250256/TdsoCIDFzokCEJD1kLkB',
                    'event_callback': callback
                });
                return false;
              }
              gtag_report_conversion();
          `}
          </script>
        </Helmet>
      )}
      {!canSignIn && <Splash />}
      <AmplifyAuthenticator
        style={{
          '--amplify-primary-color': theme.palette.primary.main,
          '--amplify-primary-tint': theme.palette.primary.main,
          '--amplify-primary-shade': theme.palette.primary.main,
          // Initially hide sign in, until we've made sure the DataStore has been cleared
          display: canSignIn ? 'inherit' : 'none'
        }}
        initialAuthState={showSignUp ? AuthState.SignUp : AuthState.SignIn}
      >
        <AmplifySignUp
          slot='sign-up'
          headerText='Create your free Getting Focused account'
          formFields={FORM_FIELDS}
        />
        <AmplifySignIn
          slot='sign-in'
          headerText='Sign in to start Getting Focused'
          hideSignUp={isStaging() || GUEST_ACCESS_ONLY}
          formFields={FORM_FIELDS}
        />
        {authState === 'signedin' && (
          <Elements stripe={stripePromise}>
            <div className={classes.root}>
              <CssBaseline />
              <GettingFocusedApp history={history} />
            </div>
          </Elements>
        )}
      </AmplifyAuthenticator>
      {guestPrompt && authState !== AuthState.SignedIn && (
        <GuestSignUp open={true} />
      )}
      {betaPrompt && authState !== AuthState.SignedIn && (
        <BetaSignUp open={true} onClose={() => setBetaPrompt(false)} />
      )}
      {(!betaPrompt || !isMobileOnly || !isLandscape) && (
        <CookieWarning
          open={warnCookies}
          messagePost={
            <span>
              By signing up you accept our <u>Terms &amp; Conditions</u>
            </span>
          }
          onConfirm={() => {
            window.open('https://www.gettingfocused.com/support/terms.html')
          }}
        />
      )}
    </MuiThemeProvider>
  )
}

export default withOrientationChange(withRouter(App))
